import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const departmentKeys = {
  all: ['departments'],
  lists: () => [...departmentKeys.all, 'list'],
  details: () => [...departmentKeys.all, 'detail'],
  detail: (id) => [...departmentKeys.details(), id],
};

export const useDepartments = () => {
  return useQuery({
    queryKey: departmentKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('departments');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useDepartment = (id, options = {}) => {
  return useQuery({
    queryKey: departmentKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`departments/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

export const useCreateDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (departmentData) => {
      const response = await apiClient.post('departments', departmentData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating department:', error);
    }
  });
};

export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (departmentData) => {
      const response = await apiClient.put(`departments/${departmentData.uuid}`, departmentData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(departmentKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating department:', error);
    }
  });
};

export const useDeleteDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`departments/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
      queryClient.removeQueries({ queryKey: departmentKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting department:', error);
    }
  });
};